import { yupResolver } from '@hookform/resolvers/yup'
import { t } from '@if/core/i18n'
import { EPaymentFrequency } from '@if/core/types'
import { dateOnly, InferType, mixed, object, ref } from '@if/core/yup'
import { useUser } from '@src/providers/UserProvider'
import { SUPPORTED_FREQUENCIES } from '@src/types/constants'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { DateOnly } from 'node_modules/@if/core/dist/date/dateOnly'
import { useMemo } from 'react'
import { Resolver } from 'react-hook-form'
import { isNewFirstPaymentValidForIntervals } from './LoanCalendarFrequency-selectors'

export const getNewFrequencySchema = (canBypassAcpLock: boolean) =>
  object({
    frequency: mixed<EPaymentFrequency>()
      .oneOf(SUPPORTED_FREQUENCIES)
      .required()
      .test(
        'test-sameFrequency',
        () => t('loan.payment.editPaymentFrequency.errors.sameFrequency'),
        (value, context) => {
          const firstPaymentDate = context.resolve<DateOnly | null>(ref('firstPaymentDate'))
          if (!firstPaymentDate) return true // If first payment date is not set, we cannot validate frequency

          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))
          const currentPaymentConfig = simulation.getRepaymentConfigAtDate(firstPaymentDate)

          // Get the last payment config
          const lastPaymentConfig = simulation.fullLoan.paymentConfigs[simulation.fullLoan.paymentConfigs.length - 1]

          // Only validate if the current config is the last one AND frequency is the same
          if (currentPaymentConfig?.id === lastPaymentConfig?.id && value === currentPaymentConfig.frequency) {
            return false
          }

          return true
        },
      ),
    firstPaymentDate: dateOnly()
      .required()
      .test(
        'test-inIntervals',
        () => t('loan.payment.editPaymentFrequency.errors.firstPaymentDateInvalid'),
        (value, context) => {
          return isNewFirstPaymentValidForIntervals(
            context.resolve<CalendarSimulation>(ref('$simulation')),
            context.resolve<EPaymentFrequency>(ref('frequency')),
            value,
            canBypassAcpLock,
          )
        },
      ),
  })

export interface NewFrequencySchemaContext {
  simulation: CalendarSimulation
}

export type NewFrequencySchemaType = InferType<ReturnType<typeof getNewFrequencySchema>>

export const useNewFrequencyResolver = () => {
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  return useMemo<Resolver<NewFrequencySchemaType, NewFrequencySchemaContext>>(
    () => yupResolver(getNewFrequencySchema(canBypassAcpLock)),
    [canBypassAcpLock],
  )
}
